/**
 * RevenueCat SDK wrapper for Capacitor (iOS/Android)
 * Handles in-app purchases and subscription management on native platforms.
 * Only loaded on native — web uses Stripe.
 */

import { platform } from './native';
import { logger } from './logger';
import { CREDIT_PACK_SMALL, CREDIT_PACK_LARGE } from '../constants/pricing';

// RevenueCat product ID → tier mapping
const PRODUCT_TIER_MAP = {
  // iOS (App Store)
  reedle_pro_monthly: { tier: 'pro', billingCycle: 'monthly', periodMonths: 1 },
  reedle_pro_annual: { tier: 'pro', billingCycle: 'annual', periodMonths: 12 },
  reedle_max_monthly: { tier: 'max', billingCycle: 'monthly', periodMonths: 1 },
  reedle_max_annual: { tier: 'max', billingCycle: 'annual', periodMonths: 12 },
  // Android (Google Play) — subscriptionId:basePlanId format
  'reedle_pro:monthly': { tier: 'pro', billingCycle: 'monthly', periodMonths: 1 },
  'reedle_pro:annual': { tier: 'pro', billingCycle: 'annual', periodMonths: 12 },
  'reedle_max:monthly': { tier: 'max', billingCycle: 'monthly', periodMonths: 1 },
  'reedle_max:annual': { tier: 'max', billingCycle: 'annual', periodMonths: 12 },
};

// Credit pack product IDs → credits mapping (non-renewing/consumable)
// Same IDs on iOS and Android (one-time products don't use subscriptionId:basePlanId format)
const CREDIT_PRODUCT_MAP = {
  reedle_credits_1000: { credits: CREDIT_PACK_SMALL },
  reedle_credits_5000: { credits: CREDIT_PACK_LARGE },
};

// Reverse mapping: credits amount → product ID
const CREDITS_PRODUCT_MAP = {
  [CREDIT_PACK_SMALL]: 'reedle_credits_1000',
  [CREDIT_PACK_LARGE]: 'reedle_credits_5000',
};

// Reverse mapping: tier + billingCycle → platform-specific product ID
const TIER_PRODUCT_MAP = {
  'pro:monthly': { ios: 'reedle_pro_monthly', android: 'reedle_pro:monthly' },
  'pro:annual': { ios: 'reedle_pro_annual', android: 'reedle_pro:annual' },
  'max:monthly': { ios: 'reedle_max_monthly', android: 'reedle_max:monthly' },
  'max:annual': { ios: 'reedle_max_annual', android: 'reedle_max:annual' },
};

let Purchases = null;

// Cached map of tier:billingCycle → localizedPriceString from StoreKit/Play
// e.g., { 'pro:monthly': '$9.99', 'pro:annual': '$99.99', ... }
// Populated by prefetchOfferings() after initRevenueCat.
let cachedNativePrices = {};

/**
 * Lazily import the RevenueCat SDK (only on native).
 * Sets the module-level Purchases variable — call await ensurePurchases()
 * then use Purchases directly. We can't return the plugin from an async
 * function because Capacitor plugin proxies are thenables, which causes
 * "Purchases.then() is not implemented" errors.
 */
async function ensurePurchases() {
  if (Purchases) return;
  const mod = await import('@revenuecat/purchases-capacitor');
  Purchases = mod.Purchases;
}

/**
 * Pre-fetch RC offerings and cache localizedPriceString for each subscription/credit product.
 * Called fire-and-forget after initRevenueCat. By the time the user opens a purchase modal,
 * the cache is populated and getNativePrices() returns instantly (sync).
 */
export async function prefetchOfferings() {
  if (!platform.isNative) return;
  try {
    const offerings = await getOfferings();
    if (!offerings?.current?.availablePackages) return;
    const prices = {};
    for (const pkg of offerings.current.availablePackages) {
      const productId = pkg.product?.identifier;
      const priceStr = pkg.product?.localizedPriceString ?? pkg.product?.priceString ?? null;
      if (!productId || !priceStr) continue;
      // Map subscription products via PRODUCT_TIER_MAP
      const tierInfo = PRODUCT_TIER_MAP[productId];
      if (tierInfo) {
        prices[`${tierInfo.tier}:${tierInfo.billingCycle}`] = priceStr;
      }
      // Map credit pack products directly by product ID
      if (CREDIT_PRODUCT_MAP[productId]) {
        prices[productId] = priceStr;
      }
    }
    cachedNativePrices = prices;
    logger.info('RC native prices cached', { keys: Object.keys(prices) });
  } catch (error) {
    logger.error('Failed to prefetch RC offerings for price cache', error);
    // Non-fatal — UI falls back to '—' for native prices
  }
}

/**
 * Returns the cached native price map (sync, safe to call any time).
 * Keys: 'pro:monthly', 'pro:annual', 'max:monthly', 'max:annual',
 *       'reedle_credits_1000', 'reedle_credits_5000'
 * Values: localizedPriceString from StoreKit/Play (e.g., '$9.99', '¥68')
 * Returns empty object if prefetchOfferings() hasn't completed yet.
 */
export function getNativePrices() {
  return cachedNativePrices;
}

/**
 * Initialize RevenueCat SDK. Must be called after user authentication.
 * Sets the app_user_id to our Supabase user ID so webhooks can identify the user.
 */
export async function initRevenueCat(userId) {
  if (!platform.isNative) return;

  try {
    await ensurePurchases();
    const apiKey = platform.isIOS
      ? import.meta.env.VITE_REVENUECAT_API_KEY_APPLE
      : import.meta.env.VITE_REVENUECAT_API_KEY_GOOGLE;

    if (!apiKey) {
      logger.error('RevenueCat API key not configured for platform:', platform.name);
      return;
    }

    await Purchases.configure({ apiKey, appUserID: userId });
    logger.info('RevenueCat initialized', { userId, platform: platform.name });
  } catch (error) {
    logger.error('Failed to initialize RevenueCat', error);
  }
}

/**
 * Get current RevenueCat offerings (products + prices from stores).
 * Prices are dynamic — set in App Store Connect / Google Play Console.
 */
export async function getOfferings() {
  if (!platform.isNative) return null;

  try {
    await ensurePurchases();
    const result = await Purchases.getOfferings();
    const offerings = result.offerings || result;
    return offerings;
  } catch (error) {
    logger.error('Failed to get RevenueCat offerings', error);
    return null;
  }
}

/**
 * Find a specific package from offerings by tier and billing cycle.
 * Returns the RC package object needed for purchasePackage().
 */
export async function findPackage(tier, billingCycle) {
  const offerings = await getOfferings();
  if (!offerings?.current) return null;

  const mapping = TIER_PRODUCT_MAP[`${tier}:${billingCycle}`];
  if (!mapping) return null;
  const productId = platform.isAndroid ? mapping.android : mapping.ios;

  // Search through all available packages in the current offering
  const allPackages = offerings.current.availablePackages || [];
  return allPackages.find(pkg => pkg.product?.identifier === productId) || null;
}

/**
 * Find a credit pack package from offerings by credit amount.
 * Returns the RC package object needed for purchasePackage().
 */
export async function findCreditPackage(credits) {
  const offerings = await getOfferings();
  if (!offerings?.current) return null;

  const productId = CREDITS_PRODUCT_MAP[credits];
  if (!productId) return null;

  const allPackages = offerings.current.availablePackages || [];
  return allPackages.find(pkg => pkg.product?.identifier === productId) || null;
}

/**
 * Purchase a package (triggers native payment sheet).
 * Returns { success, customerInfo } or { success: false, cancelled, error }.
 */
export async function purchasePackage(pkg) {
  if (!platform.isNative) return { success: false, error: 'Not on native platform' };

  try {
    await ensurePurchases();
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    logger.info('RevenueCat purchase completed', {
      productId: pkg.product?.identifier,
    });
    return { success: true, customerInfo };
  } catch (error) {
    // User cancelled the purchase — not an error
    if (error.code === 'PURCHASE_CANCELLED_ERROR' || error.userCancelled) {
      logger.info('RevenueCat purchase cancelled by user');
      return { success: false, cancelled: true };
    }
    logger.error('RevenueCat purchase failed', error);
    return { success: false, error: error.message || 'Purchase failed' };
  }
}

/**
 * Get current customer info (entitlements, active subscriptions).
 */
export async function getCustomerInfo() {
  if (!platform.isNative) return null;

  try {
    await ensurePurchases();
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    logger.error('Failed to get RevenueCat customer info', error);
    return null;
  }
}

/**
 * Restore previous purchases (required by App Store guidelines).
 * Returns { customerInfo, hasActivePurchases } on success.
 * hasActivePurchases is true if any active entitlements were found after restore.
 * Throws on error so callers can show appropriate error UI.
 */
export async function restorePurchases() {
  if (!platform.isNative) return null;

  await ensurePurchases();
  const { customerInfo } = await Purchases.restorePurchases();
  logger.info('RevenueCat purchases restored');
  const hasActivePurchases = Object.keys(customerInfo?.entitlements?.active ?? {}).length > 0;
  return { customerInfo, hasActivePurchases };
}

/**
 * Map a RevenueCat product ID to our tier info.
 */
export function mapProductToTier(productId) {
  return PRODUCT_TIER_MAP[productId] || null;
}

/**
 * Open the platform's subscription management page.
 * iOS: App Store subscriptions, Android: Google Play subscriptions.
 * Throws on failure so callers can show an error toast.
 */
export async function openSubscriptionManagement() {
  if (!platform.isNative) return;

  try {
    await ensurePurchases();
    await Purchases.showManageSubscriptions();
  } catch (error) {
    logger.error('Failed to open subscription management', error);
    throw error;
  }
}
